/**
 * Auto-recovery for failed route-chunk dynamic imports (Vite's
 * `vite:preloadError`, dispatched by the build's `__vitePreload` wrapper).
 *
 * Reloading is throttled to at most once per minute per tab, and only while
 * online. Pure decision logic lives here so it can be unit-tested without
 * touching `window`/`sessionStorage` directly; `main.tsx` wires it to the
 * real event + storage.
 */

const THROTTLE_MS = 60_000;
const STORAGE_KEY = 'tk-lms:preload-reload-at';

export interface PreloadErrorDecisionInput {
  online: boolean;
  lastReloadAt: number | null;
  now: number;
}

/**
 * Pure guard: should this `vite:preloadError` trigger a reload?
 * No — offline, or throttled (<60s since the last reload attempt this tab made).
 */
export function shouldReloadOnPreloadError(input: PreloadErrorDecisionInput): boolean {
  if (!input.online) return false;
  if (input.lastReloadAt !== null && input.now - input.lastReloadAt < THROTTLE_MS) return false;
  return true;
}

export function readLastReloadAt(): number | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { at?: number };
    return typeof parsed.at === 'number' ? parsed.at : null;
  } catch {
    return null;
  }
}

export function recordReloadAttempt(now: number): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ at: now }));
  } catch {
    // sessionStorage unavailable (private mode, quota) — nothing to record.
  }
}
